import { useCallback, useEffect, useState } from "react";
import Layout from "layout/Layout";
import DateRangeSelector from "pages/dashboard-overview/components/DateRangeSelector";
import useHttp from "hooks/useHttp";
import useAnalyticsFilters from "hooks/useAnalyticsFilters";
import FunnelVisualization from "pages/analytics/components/FunnelVisualization";
import LeadConversionSummary from "pages/analytics/sales-funnel/components/LeadConversionSummary";
import EmailAnalyticsSection from "pages/analytics/sales-funnel/components/EmailAnalyticsSection";
import VslAnalyticsSection from "pages/analytics/sales-funnel/components/VslAnalyticsSection";
import GhlFunnelSyncButton from "pages/analytics/sales-funnel/components/GhlFunnelSyncButton";
import {
  getFunnelMetrics,
  getPipelineDistribution,
} from "services/funnel-analytics.service";
import { isCustomRangeIncomplete } from "utils/analyticsQueryParams";

export default function FunnelPerformance() {
  const api = useHttp();
  const { filters, dateRange, setDateRange, customRange, setCustomRange } =
    useAnalyticsFilters("thisMonth");
  const [metrics, setMetrics] = useState(null);
  const [metricsLoading, setMetricsLoading] = useState(true);
  const [metricsError, setMetricsError] = useState(null);
  const [pipelineDistribution, setPipelineDistribution] = useState([]);
  const [pipelineDistributionLoading, setPipelineDistributionLoading] = useState(true);
  const [pipelineDistributionError, setPipelineDistributionError] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const loadDashboard = useCallback(async () => {
    if (isCustomRangeIncomplete(filters)) {
      setMetricsLoading(false);
      setMetrics(null);
      setMetricsError(null);
      setPipelineDistributionLoading(false);
      setPipelineDistribution([]);
      setPipelineDistributionError(null);
      return;
    }

    setMetricsLoading(true);
    setMetricsError(null);
    setPipelineDistributionLoading(true);
    setPipelineDistributionError(null);

    const [metricsResult, distributionResult] = await Promise.allSettled([
      getFunnelMetrics(api, { filters }),
      getPipelineDistribution(api, { filters }),
    ]);

    if (metricsResult.status === "fulfilled") {
      setMetrics(metricsResult.value);
    } else {
      setMetrics(null);
      setMetricsError("Failed to load funnel metrics. Please try again.");
    }
    setMetricsLoading(false);

    if (distributionResult.status === "fulfilled") {
      setPipelineDistribution(distributionResult.value?.distribution ?? []);
    } else {
      setPipelineDistribution([]);
      setPipelineDistributionError("Failed to load funnel visualization data.");
    }
    setPipelineDistributionLoading(false);
  }, [api, filters]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard, refreshKey]);

  const handleSynced = useCallback(() => {
    setRefreshKey((key) => key + 1);
  }, []);

  const metricsProps = { metrics, metricsLoading, metricsError };
  const pipelineDistributionProps = {
    pipelineDistribution,
    pipelineDistributionLoading,
    pipelineDistributionError,
  };

  return (
    <Layout>
      <div className="mb-4 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Funnel Performance</h1>
          <p className="text-sm text-gray-500 mt-1">
            BV1 funnel conversion, VSL analytics, and email metrics
          </p>
        </div>
        <GhlFunnelSyncButton api={api} onSynced={handleSynced} />
      </div>

      <div className="mb-4 flex flex-col gap-4 sm:flex-row sm:justify-between">
        <DateRangeSelector
          selected={dateRange}
          onChange={setDateRange}
          customRange={customRange}
          onCustomRangeChange={setCustomRange}
        />
      </div>

      <LeadConversionSummary filters={filters} {...metricsProps} />

      <div className="mb-8">
        <FunnelVisualization api={api} filters={filters} funnelId="main_offer" hideComparison {...metricsProps} {...pipelineDistributionProps} />
        <div className="mt-6">
          <FunnelVisualization api={api} filters={filters} funnelId="blueprint" hideComparison {...metricsProps} {...pipelineDistributionProps} />
        </div>
        <div className="mt-6">
          <FunnelVisualization api={api} filters={filters} funnelId="mentorship" hideComparison {...metricsProps} {...pipelineDistributionProps} />
        </div>
        <div className="mt-6">
          <FunnelVisualization api={api} filters={filters} funnelId="inner_circle" hideComparison {...metricsProps} {...pipelineDistributionProps} />
        </div>
        <div className="mt-6">
          <FunnelVisualization api={api} filters={filters} funnelId="academy" hideComparison {...metricsProps} {...pipelineDistributionProps} />
        </div>
      </div>

      <div className="mb-8">
        <VslAnalyticsSection api={api} filters={filters} refreshKey={refreshKey} />
      </div>

      <div className="mb-8">
        <EmailAnalyticsSection
          api={api}
          filters={filters}
          refreshKey={refreshKey}
          onSynced={handleSynced}
        />
      </div>
    </Layout>
  );
}
